import React, { useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Navbar } from '../Navbar';
import UniversalMessageIcon from './messaging/UniversalMessageIcon';
import '../styles/layout.css';

/**
 * Main layout component
 * 
 * This component provides the common layout structure for all pages,
 * including the navigation bar, floating message icon, and an outlet for page content.
 */
const Layout = () => {
  const { pathname } = useLocation();
  const mainRef = useRef(null);

  /**
   * ─── ⭐ A NEW PAGE STARTS AT ITS TOP ────────────────────────────────────────────────────────
   *
   * `.main-content` is the app's scrolling element — the window never scrolls, because
   * `.app-container` is a fixed-height flex column and the nav is a SIBLING of this region rather
   * than a banner over it. And this element belongs to Layout, which does not unmount between
   * routes, so its scrollTop survived every navigation: arrive from a page you had scrolled down,
   * and the next page opened at that same offset with its heading already above the fold.
   *
   * ⚠️ WHICH LOOKS LIKE A LAYOUT BUG AND IS NOT. The reported symptom was a page "rendering under
   * the header", intermittently and usually on the first visit — the title was not underneath the
   * nav, it was scrolled past it, and it only happened when the previous page had been scrolled.
   * Hours can be spent on z-index and padding for this.
   *
   * ⓘ Keyed on `pathname` alone: a change of query string or hash is the same page, and resetting
   * on those would fight a page that pages or filters through the URL.
   *
   * ⛔ `scrollTop = 0`, not `scrollIntoView` or `window.scrollTo` — the window has nothing to
   * scroll, and scrollIntoView on a child would move whichever ancestor it found first.
   */
  useEffect(() => {
    const el = mainRef.current;
    if (el) el.scrollTop = 0;
  }, [pathname]);

  return (
    <div className="app-container">
      <Navbar />
      <main className="main-content landing-padding" ref={mainRef}>
        <Outlet />
      </main>

      {/* Global floating message icon — visible for all logged-in users on every page */}
      <UniversalMessageIcon position="top-right" />

      {/* ProcessGuide is NOT mounted here any more. It used to live in Layout and prefix-match
          the pathname, which never fired for the two pages it was written for: both are normally
          reached as dashboard sub-pages, where the URL stays /dashBoard and the page is chosen
          by a subPageId. Each page now mounts it with the key of the guide it wants. */}
    </div>
  );
};

export default Layout;
